import { apiService } from '../../../components/api/apiService';
import { logGovernedMcp } from './governedMcpLogger';
import type { WorkflowSource } from './governedMcpTypes';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/workflows';

export interface WorkflowListRow {
  id: string;
  tenantId: string;
  name: string;
  description: string;
  code: string;
  status: string;
  projectId: string | null;
  parentWorkflowId: string | null;
  version: number;
  tags: string[];
  activationStatus: string;
  icon: string;
  channels: {
    sandbox: unknown;
    staging: unknown;
    production: unknown;
  };
  createdAt: string | null;
  updatedAt: string | null;
}

function serviceErrorMessage(err: unknown): string {
  const axiosErr = err as {
    code?: string;
    response?: { data?: { error?: { message?: string }; message?: string }; status?: number };
    message?: string;
  };
  if (axiosErr.code === 'ERR_NETWORK' || axiosErr.message === 'Network Error') {
    return 'Cannot reach BackendPYApp at http://127.0.0.1:8014. Start it with `python run.py` from BackendPYApp.';
  }
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'Failed to load workflows'
  );
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item).trim()).filter(Boolean);
}

function mapRow(raw: Record<string, unknown>): WorkflowListRow {
  const channelsRaw = (raw.channels as Record<string, unknown> | undefined) || {};
  return {
    id: String(raw.id ?? ''),
    tenantId: String(raw.tenantId ?? raw.tenant_id ?? ''),
    name: String(raw.name ?? ''),
    description: String(raw.description ?? ''),
    code: String(raw.code ?? ''),
    status: String(raw.status ?? 'draft'),
    projectId: raw.projectId == null && raw.project_id == null ? null : String(raw.projectId ?? raw.project_id),
    parentWorkflowId:
      raw.parentWorkflowId == null && raw.parent_workflow_id == null
        ? null
        : String(raw.parentWorkflowId ?? raw.parent_workflow_id),
    version: Number(raw.version) || 0,
    tags: asStringArray(raw.tags),
    activationStatus: String(raw.activationStatus ?? raw.activation_status ?? 'active'),
    icon: String(raw.icon ?? 'workflow'),
    channels: {
      sandbox: channelsRaw.sandbox ?? null,
      staging: channelsRaw.staging ?? null,
      production: channelsRaw.production ?? null,
    },
    createdAt: raw.createdAt == null && raw.created_at == null ? null : String(raw.createdAt ?? raw.created_at),
    updatedAt: raw.updatedAt == null && raw.updated_at == null ? null : String(raw.updatedAt ?? raw.updated_at),
  };
}

export function toWorkflowSource(row: WorkflowListRow, longRunning = false): WorkflowSource {
  return {
    id: row.id,
    name: row.name,
    code: row.code || row.id,
    description: row.description,
    longRunning,
    destructive: false,
    hasSideEffects: true,
    isIdempotent: false,
    requiresApproval: false,
    status: row.status,
    activationStatus: row.activationStatus,
    version: row.version,
    tags: row.tags,
  };
}

export const workflowListService = {
  async list(): Promise<WorkflowListRow[]> {
    logGovernedMcp('app', 'list_workflows');
    try {
      const data = (await apiService.get(BASE, {}, CONNECTION)) as Record<string, unknown>;
      const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
      const rows = items
        .map((item) => mapRow(item as Record<string, unknown>))
        .filter((row) => row.id);
      logGovernedMcp('app', 'list_workflows_ok', { count: rows.length });
      return rows;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'list_workflows_failed', { message });
      throw new Error(message);
    }
  },
};
